import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, useDroppable } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Save, Plus, ArrowLeft, ExternalLink, Home, ChevronRight } from 'lucide-react';
import { doc, setDoc, getDoc, addDoc, collection } from 'firebase/firestore';
import { db } from '../firebase';
import { componentRegistry } from '../builder-components';
import ComponentLibrary from './ComponentLibrary';
import SortableComponent from './SortableComponent';
import StylePanel from './StylePanel';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const DroppableContainerZone = ({ containerId, children, isEmpty }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: `container-${containerId}`,
    data: { type: 'container', containerId }
  });

  return (
    <div
      ref={setNodeRef}
      className={`min-h-[80px] border-2 border-dashed rounded-lg transition-all ${
        isOver
          ? 'border-primary bg-primary/5'
          : isEmpty
            ? 'border-gray-300 bg-gray-50'
            : 'border-transparent'
      }`}
    >
      {children}
      {isEmpty && (
        <div className="flex items-center justify-center h-20 text-gray-400 text-sm">
          <Plus size={16} className="mr-1" />
          Drop components here
        </div>
      )}
    </div>
  );
};

const Breadcrumbs = ({ items }) => (
  <nav className="flex items-center space-x-2 text-sm text-gray-500">
    <Link to="/cms" className="hover:text-gray-900 flex items-center">
      <Home size={14} className="mr-1" />
      CMS
    </Link>
    {items.map((item, index) => (
      <React.Fragment key={index}>
        <ChevronRight size={14} />
        {item.href ? (
          <Link to={item.href} className="hover:text-gray-900">{item.label}</Link>
        ) : (
          <span className="text-gray-900 font-medium">{item.label}</span>
        )}
      </React.Fragment>
    ))}
  </nav>
);

const renderComponent = (component, onSelectChild, onDeleteChild) => {
  const Component = componentRegistry[component.type];
  if (!Component) {
    return (
      <div className="p-4 border border-dashed border-red-500 bg-red-50 text-red-700 rounded">
        Unknown component: {component.type}
      </div>
    );
  }

  const isContainer = Component.builderConfig?.isContainer;

  if (isContainer) {
    const hasChildren = component.children && component.children.length > 0;
    return (
      <Component {...component.props}>
        <DroppableContainerZone containerId={component.id} isEmpty={!hasChildren}>
          {hasChildren && (
            <div className="space-y-2 p-2">
              {component.children.map((child) => (
                <div key={child.id} className="relative group/child border border-dashed border-gray-300 hover:border-primary rounded p-2 bg-white">
                  <div className="absolute -top-6 right-0 opacity-0 group-hover/child:opacity-100 flex gap-1 bg-white shadow rounded px-1 py-0.5 z-10">
                    <button
                      onClick={() => onSelectChild(child)}
                      className="text-xs text-gray-600 hover:text-gray-900 px-1"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => onDeleteChild(component.id, child.id)}
                      className="text-xs text-red-600 hover:text-red-800 px-1"
                    >
                      ×
                    </button>
                  </div>
                  {renderComponent(child, onSelectChild, onDeleteChild)}
                </div>
              ))}
            </div>
          )}
        </DroppableContainerZone>
      </Component>
    );
  }

  return <Component {...component.props} />;
};

const PageBuilder = ({ pageId: propPageId }) => {
  const { pageId: urlPageId } = useParams();
  const pageId = propPageId || urlPageId;
  const navigate = useNavigate();
  const isNewPage = !pageId || pageId === 'new';
  
  const [components, setComponents] = useState([]);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [showLibrary, setShowLibrary] = useState(true);
  const [showStylePanel, setShowStylePanel] = useState(false);
  const [pageSettings, setPageSettings] = useState({
    title: '',
    slug: '',
    metaDescription: '',
    status: 'draft'
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNewPage);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (pageId && !isNewPage) {
      loadPage();
    }
  }, [pageId, isNewPage]);

  const loadPage = async () => {
    try {
      setLoading(true);
      const pageDoc = await getDoc(doc(db, 'cms_pages', pageId));
      if (pageDoc.exists()) {
        const data = pageDoc.data();
        setComponents(data.components || []);
        setPageSettings({
          title: data.title || '',
          slug: data.slug || '',
          metaDescription: data.seo?.metaDescription || '',
          status: data.status || 'draft'
        });
      } else {
        toast.error('Page not found');
        navigate('/cms/pages');
      }
    } catch (error) {
      console.error('Error loading page:', error);
      toast.error('Failed to load page');
    } finally {
      setLoading(false);
    }
  };

  const savePage = async () => {
    if (!pageSettings.title.trim()) {
      toast.error('Please enter a page title');
      return;
    }
    if (!pageSettings.slug.trim()) {
      toast.error('Please enter a page slug');
      return;
    }

    try {
      setSaving(true);
      const pageData = {
        title: pageSettings.title,
        slug: pageSettings.slug.toLowerCase().replace(/[^a-z0-9-]/g, '-'),
        components: components,
        seo: {
          metaDescription: pageSettings.metaDescription
        },
        status: pageSettings.status,
        updatedAt: new Date()
      };

      if (isNewPage) {
        pageData.createdAt = new Date();
        const docRef = await addDoc(collection(db, 'cms_pages'), pageData);
        toast.success('Page created successfully!');
        navigate(`/cms/pages/${docRef.id}`);
      } else {
        await setDoc(doc(db, 'cms_pages', pageId), pageData, { merge: true });
        toast.success('Page saved successfully!');
      }
    } catch (error) {
      console.error('Error saving page:', error);
      toast.error('Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;

    if (over.data?.current?.type === 'container') {
      const containerId = over.data.current.containerId;
      const draggedId = active.id;
      const draggedComponent = components.find(c => c.id === draggedId);
      if (!draggedComponent || draggedId === containerId) return;

      setComponents(prev => {
        const filtered = prev.filter(c => c.id !== draggedId);
        return filtered.map(c => {
          if (c.id === containerId) {
            return {
              ...c,
              children: [...(c.children || []), draggedComponent]
            };
          }
          return c;
        });
      });
      return;
    }

    if (active.id !== over.id) {
      setComponents((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return items;
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const addComponent = (componentType, parentId = null) => {
    const componentConfig = componentRegistry[componentType];
    const isContainer = componentConfig?.builderConfig?.isContainer || false;

    const newComponent = {
      id: `component-${Date.now()}`,
      type: componentType,
      props: {},
      styles: {},
      children: isContainer ? [] : undefined,
      order: components.length
    };

    if (parentId) {
      setComponents(components.map(comp => {
        if (comp.id === parentId) {
          return { ...comp, children: [...(comp.children || []), newComponent] };
        }
        return comp;
      }));
    } else {
      setComponents([...components, newComponent]);
    }
  };

  const updateComponent = (id, updates) => {
    const updateInTree = (items) => {
      return items.map(comp => {
        if (comp.id === id) {
          return { ...comp, ...updates };
        }
        if (comp.children) {
          return { ...comp, children: updateInTree(comp.children) };
        }
        return comp;
      });
    };
    setComponents(updateInTree(components));
  };

  const deleteComponent = (id) => {
    setComponents(components.filter(comp => comp.id !== id));
    if (selectedComponent?.id === id) {
      setSelectedComponent(null);
    }
  };

  const deleteChildComponent = (parentId, childId) => {
    setComponents(components.map(comp => {
      if (comp.id === parentId) {
        return {
          ...comp,
          children: (comp.children || []).filter(child => child.id !== childId)
        };
      }
      return comp;
    }));
    if (selectedComponent?.id === childId) {
      setSelectedComponent(null);
    }
  };

  const selectComponent = (component) => {
    setSelectedComponent(component);
    setShowStylePanel(true);
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading page...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex bg-gray-100">
      {/* Component Library Sidebar */}
      {showLibrary && (
        <ComponentLibrary
          onAddComponent={(type) => addComponent(type)}
          onClose={() => setShowLibrary(false)}
        />
      )}

      {/* Main Canvas */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Toolbar */}
        <div className="bg-white border-b px-6 py-4">
          <div className="mb-3">
            <Breadcrumbs items={[
              { label: 'Pages', href: '/cms/pages' },
              { label: isNewPage ? 'New Page' : (pageSettings.title || 'Edit Page') }
            ]} />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/cms/pages')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft size={20} />
              </button>
              <Button
                onClick={() => setShowLibrary(!showLibrary)}
                className="gap-2"
              >
                <Plus size={20} />
                Add Component
              </Button>
              <div className="flex flex-col">
                <Input
                  type="text"
                  value={pageSettings.title}
                  onChange={(e) => setPageSettings({ ...pageSettings, title: e.target.value })}
                  placeholder="Page Title"
                  className="font-medium"
                />
                <div className="flex items-center mt-1">
                  <span className="text-xs text-gray-500 mr-1">/</span>
                  <Input
                    type="text"
                    value={pageSettings.slug}
                    onChange={(e) => setPageSettings({ ...pageSettings, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                    placeholder="page-slug"
                    className="text-xs font-mono h-7"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {!isNewPage && pageSettings.slug && (
                <button
                  onClick={() => window.open(`/${pageSettings.slug}`, '_blank')}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <ExternalLink size={20} />
                </button>
              )}
              <Select
                value={pageSettings.status}
                onValueChange={(value) => setPageSettings({ ...pageSettings, status: value })}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                </SelectContent>
              </Select>
              <Button
                onClick={savePage}
                disabled={saving}
                className="gap-2"
              >
                <Save size={20} />
                {saving ? 'Saving...' : isNewPage ? 'Create' : 'Save'}
              </Button>
            </div>
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 overflow-y-auto bg-gray-50 p-8">
          <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-lg min-h-full p-8">
            {components.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-gray-400 text-lg mb-4">No components yet</p>
                <button
                  onClick={() => setShowLibrary(true)}
                  className="text-primary hover:underline"
                >
                  Add your first component
                </button>
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={components.map(c => c.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-4">
                    {components.map((component) => (
                      <SortableComponent
                        key={component.id}
                        component={component}
                        onSelect={selectComponent}
                        onDelete={deleteComponent}
                      >
                        {renderComponent(component, selectComponent, deleteChildComponent)}
                      </SortableComponent>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>
      </div>

      {/* Style Panel */}
      {showStylePanel && selectedComponent && (
        <StylePanel
          component={selectedComponent}
          onUpdate={(updates) => updateComponent(selectedComponent.id, updates)}
          onClose={() => setShowStylePanel(false)}
        />
      )}
    </div>
  );
};

export default PageBuilder;
